/* Protection Notice Report — a per-pallet PDF annex for the "Send
   Protection Notice" shipper email (InvoiceRegistry.js). Same
   build-once / reuse pattern as paymentConfirmation.js: an HTML
   builder, a Blob wrapper (htmlToPdfBlob) shared by the download
   button and the auto-attached email copy so both are byte-identical,
   and a React preview modal (iframe + Print/Download/Close).

   2026-09-14, Arturo: "generar una especie de reporte de proteccion
   que se anexe automatico en PDF que incluya el notice of protection,
   una linea por pallet con la informacion relevante... que se obtiene
   del packing list del embarque, datos de ground-fresh y shipper en
   la parte de arriba, fecha y hora de notificacion al shipper impresa
   en el mismo pdf, poder previsualizar este pdf antes de enviar y
   poder descargarlo... con el nombre Protection Notice {PU} {factura
   shipper} {nombre shipper}" — plus a same-day follow-up asking for
   GFI's own PO#/PU# to also appear at the top, before the pallet
   table (already in the email body; added here too for a document
   that has to stand on its own once printed/forwarded). */

import { useState } from "react";
import { createPortal } from "react-dom";
import { LOGO_B64 } from "./SalesReports";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";

function escHtml(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function fmtDateTimeLong(d) {
  if (!d) return "—";
  var dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }) + " · " + dt.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit" });
}

var GFI = {
  name: "Ground Fresh International, Inc.",
  lines: ["152 W Hoffman Ave Ste 5", "Lindenhurst, NY 11757", "USA"],
};

/* Same colors as SALE_STATUS_COLORS (InvoiceRegistry.js/
   ShipmentRegistry.js), duplicated here per this codebase's per-file
   convention — protection=red, pas=orange (2026-09-12: "PAS... menos
   severo que Protection pero mas severo que Partial Protection").
   2026-09-14 fix — Arturo: "el protection notice que le envio el
   shipper... no marca el status ni los colores correspondientes en
   los pallets" — buildProtectionReportRows already carried
   `saleStatus` per row, but the table never rendered it. */
var STATUS_COLORS = {
  protection: { bg: "#FEE2E2", text: "#DC2626" },
  pas: { bg: "#FEF3C7", text: "#D97706" },
};
var STATUS_LABELS_ES = { protection: "Protección", pas: "PAS" };
function statusCellHtml(status) {
  var c = STATUS_COLORS[status];
  if (!c) return '<span style="color:#999">—</span>';
  return '<span style="display:inline-block;padding:2px 10px;border-radius:10px;font-weight:700;background:' + c.bg + ';color:' + c.text + '">' + (STATUS_LABELS_ES[status] || status) + '</span>';
}

/* Twin River tolerance-sheet defects (2026-10-15, 3rd round) — Arturo:
   "el aviso de proteccion se puede seguir enviando desde donde se
   manda actualmente, solo quiero agregar el detalle de cada pallet
   con los defectos encontrados en cada uno, igual mostrando los
   defectos debajo de cada pallet con los colores de cada defecto,
   recuerda solo aplica para twin river" — a SEPARATE system from the
   PROTECTION_DEFECTS block above (which stays as-is, sourced from
   invoice.palletAllocations[].defects, used for every client's
   Protection Notice today). This one mirrors ShipmentRegistry.js's
   shipment.palletManifest[i].qcDefects (TOLERANCE_DEFECTS_BY_PRODUCT)
   — same colors/labels, duplicated here per this codebase's per-file
   convention — gated to ClariFresh (Twin River) shipments only. */
var TOLERANCE_CATEGORY_COLORS = {
  severeCondition: { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.3)", text: "#B91C1C" },
  condition: { bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.3)", text: "#B45309" },
  quality: { bg: "rgba(31,78,121,0.08)", border: "rgba(31,78,121,0.3)", text: "#1F4E79" },
  /* 2026-10-21, Arturo: "Findings" — same medium-high tone as
     ShipmentRegistry.js's TOLERANCE_CATEGORY_COLORS.findings. */
  findings: { bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.4)", text: "#C2410C" },
};
var TOLERANCE_CATEGORY_LABELS_ES = { severeCondition: "Condición Severa", condition: "Condición", quality: "Calidad", findings: "Hallazgos" };
var TOLERANCE_DEFECT_LABELS_ES = {
  decay: "Pudrición", mold: "Moho", insect: "Insecto",
  leaky: "Fuga de jugo", shrivel: "Deshidratación", soft: "Blanda", brokenSkin: "Piel rota",
  attachedRemains: "Restos adheridos", floralRemains: "Restos florales", peduncle: "Pedúnculo", offColor: "Color fuera de estándar", berryDamage: "Daño en baya",
  redBerries: "Bayas rojas", greenBerries: "Bayas verdes", scars: "Cicatrices", dryCell: "Célula seca",
  redCell: "Célula roja", regression: "Regresión", whiteCells: "Células blancas", redGreenDryTips: "Puntas rojas/verdes o secas", attachedCore: "Núcleo adherido",
  bugs: "Bichos", worms: "Gusanos", insects: "Insectos",
};
function qcDefectsRowHtml(qcDefects, colSpan) {
  if (!qcDefects) return "";
  var cats = ["severeCondition", "condition", "quality", "findings"].filter(function (cat) {
    return Array.isArray(qcDefects[cat]) && qcDefects[cat].length > 0;
  });
  if (cats.length === 0) return "";
  var pills = cats.map(function (cat) {
    var c = TOLERANCE_CATEGORY_COLORS[cat];
    var items = qcDefects[cat].map(function (e) {
      var lbl = e.key === "__custom__" ? (e.custom || "Otro") : (TOLERANCE_DEFECT_LABELS_ES[e.key] || e.key);
      var pct = (e.pct === 0 || e.pct) ? " " + e.pct + "%" : "";
      return escHtml(lbl + pct);
    }).join(", ");
    return '<span style="display:inline-block;padding:2px 8px;margin:2px 4px 2px 0;border-radius:5px;font-size:8px;font-weight:600;background:' + c.bg + ';color:' + c.text + ';border:1px solid ' + c.border + '"><b style="text-transform:uppercase;letter-spacing:0.4px">' + escHtml(TOLERANCE_CATEGORY_LABELS_ES[cat]) + '</b>: ' + items + '</span>';
  }).join("");
  return '<tr><td colspan="' + colSpan + '" style="padding:2px 6px 6px;border-bottom:1px solid #eee">' + pills + '</td></tr>';
}

/* Builds the per-protected-pallet row list, sourced from the
   shipment's own palletManifest (the "packing list del embarque"
   Arturo pointed to) joined against invoice.palletAllocations —
   mirrors buildPackingListRows in InvoiceRegistry.js (same join key:
   lot + palletNum) but scoped to only protection/pas pallets and
   carrying condition/packType too, which the packing list's own row
   shape doesn't need. Product Description follows the same auto-
   generated format ProductRegistry uses for its catalog SKUs
   ("[Product] [Units]/[Size] [Cat] [Label] Label"). */
export function buildProtectionReportRows(invoice, shipment) {
  var allocations = Array.isArray(invoice && invoice.palletAllocations)
    ? invoice.palletAllocations.filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); })
    : [];
  var manifest = (shipment && shipment.palletManifest) || [];
  return allocations.map(function (a) {
    var meta = manifest.find(function (p) { return String(p.palletNum) === String(a.palletNum); }) || {};
    var desc = [meta.productBase, (meta.unitsCase || "") + "/" + (meta.size || ""), meta.category, meta.labelInitials, "Label"].filter(Boolean).join(" ");
    return {
      palletNum: a.palletNum || "—",
      tagId: meta.tagId || "—",
      productBase: meta.productBase || "—",
      category: meta.category || "—",
      condition: meta.condition || "—",
      packType: meta.packType || "—",
      productDescription: desc || "—",
      unitsCase: meta.unitsCase || "—",
      casesPallet: meta.casesPallet || "—",
      labelInitials: meta.labelInitials || "—",
      variety: meta.variety || "—",
      saleStatus: a.saleStatus,
      /* 2026-10-13, Arturo: "los defectos de calidad deben ser a nivel
         pallet, uno podria tener cierto tipo de defectos y otro pallet
         distintos" — defects now live on the pallet allocation itself
         (a.defects), not a single flat list for the whole invoice, so
         each row can carry its own set. */
      defects: Array.isArray(a.defects) ? a.defects : [],
      /* Twin River tolerance-sheet defects (2026-10-15, 3rd round) —
         from the shipment's own palletManifest, same source
         ShipmentRegistry.js/BillRegistry.js already read live. */
      qcDefects: meta.qcDefects || null,
    };
  });
}

/* invoice: the GFI invoice (fresh). shipment: linked shipment (for
   palletManifest). supplier: the shipper record. company: Company
   Profile (protectionNoticeEs, phone). notifiedAt: Date the notice
   email is being sent — printed on the doc so the shipper sees
   exactly when GFI flagged this. clientArrivalDate/clientArrivalTime
   (2026-09-14, Arturo follow-up): when the CLIENT's own load arrived
   with them, "esto lo obtengo del reporte de calidad que me envia el
   cliente" — captured by hand on the Invoice (see InvoiceRegistry.js's
   Protection dialog) since there's no automated feed for it, printed
   here so the annex carries the full timeline (GFI's protection
   notice + when the client says the fruit actually arrived). Always
   Spanish — same override as the email body itself (shippers are
   Mexican, see CLAUDE.md). */
export const PROTECTION_DEFECTS = [
  { key: "swd", severity: "high", labelEn: "SWD (larvae)", labelEs: "SWD (larva)", impactEs: "requiere atención especial, ya que puede afectar la aceptación del pallet y, por consecuencia, el precio final del lote", impactEn: "requires special attention, as it may affect pallet acceptance and, consequently, the final price of the lot" },
  { key: "mold", severity: "high", labelEn: "Mold", labelEs: "Moho (hongo)", impactEs: "requiere atención prioritaria, ya que puede afectar la aceptación del pallet y, por consecuencia, el precio final del lote", impactEn: "requires priority attention, as it may affect pallet acceptance and, consequently, the final price of the lot" },
  { key: "decay", severity: "mediumHigh", labelEn: "Decay", labelEs: "Pudrición", impactEs: "puede tener un efecto en el precio final del lote", impactEn: "may have an effect on the final price of the lot" },
  /* 2026-10-23, Arturo: "cambia la severidad, leaky es medium high,
     soft en medium" — leaking from medium to mediumHigh, softFruit
     from low to medium (same tier-wording convention as every other
     entry at each level: mediumHigh ~ Decay, medium ~ Dry Cell/Scars/
     Red Cells). */
  { key: "leaking", severity: "mediumHigh", labelEn: "Leaking", labelEs: "Fuga de jugo", impactEs: "puede tener un efecto en el precio final del lote", impactEn: "may have an effect on the final price of the lot" },
  { key: "softFruit", severity: "medium", labelEn: "Soft fruit", labelEs: "Fruta blanda", impactEs: "puede tener un efecto menor en el precio final del lote", impactEn: "may have a minor effect on the final price of the lot" },
  { key: "discoloration", severity: "low", labelEn: "Discoloration", labelEs: "Decoloración", impactEs: "normalmente tiene un impacto mínimo en el precio final del lote", impactEn: "usually has a minimal impact on the final price of the lot" },
  /* 2026-10-21, Arturo: "chilling damage no es severidad media baja,
     es media alta y en la descripcion el impacto al precio es de
     acuerdo a la severidad" — corrected from mediumLow, and the
     impact clause no longer claims a fixed "limited" effect (that
     was wrong at mediumHigh severity) — it now says what it should
     for every other entry at this tier: depends on how severe it's
     reported. */
  { key: "chillingDamage", severity: "mediumHigh", labelEn: "Chilling Damage", labelEs: "Daño por frío", impactEs: "está relacionado con temperaturas bajas en la cadena de frío; el impacto en el precio final del lote depende de la severidad reportada", impactEn: "is related to low temperatures in the cold chain; the impact on the final price of the lot depends on the reported severity" },
  /* 2026-10-13, Arturo: "regresion no es medium to low, es medium to
     high severity" — corrected from mediumLow, same tier as Decay.
     Context (not printed — shippers already know this): regression is
     caused by transport vibration and turns the fruit from black to
     red, which is unsellable, so affected cases are typically
     discarded rather than sold at a discount — "dependiendo de la
     severidad podria generar perdida de cajas". */
  { key: "regression", severity: "mediumHigh", labelEn: "Regression", labelEs: "Regresión (vibración)", impactEs: "según la severidad, puede implicar pérdida de cajas además de un efecto en el precio final del lote", impactEn: "depending on severity, it may involve a loss of cases in addition to an effect on the final price of the lot" },
  /* 2026-10-10, Arturo: "al reporte de calidad de tr de texas hay que
     agregarle otro daño que se me habia pasado Dry Cell" + "y scars
     tambien agregalo" — "son de severidad media", same tier as
     Leaking just above. Dry Cell: individual dehydrated/underdeveloped
     drupelets, same disorder family as the tolerance sheet's
     "Red/green or dry tips" and "White cells" quality-category
     defects. Scars: surface blemishes from wind/insect/mechanical
     rubbing during growth. */
  { key: "dryCell", severity: "medium", labelEn: "Dry Cell", labelEs: "Célula seca (Dry Cell)", impactEs: "puede tener un efecto menor en el precio final del lote", impactEn: "may have a minor effect on the final price of the lot" },
  { key: "scars", severity: "medium", labelEn: "Scars", labelEs: "Cicatrices", impactEs: "puede tener un efecto menor en el precio final del lote", impactEn: "may have a minor effect on the final price of the lot" },
  /* 2026-10-21, Arturo: "agrega estos 2 Ovoripe con severidad low, Red
     cells con severidad media, y freezing damage la severidad es
     alta" — same severity-tier wording already used for existing
     defects at each tier (low ~ Soft fruit, medium ~ Dry Cell/Scars,
     high ~ SWD/Mold). */
  { key: "overripe", severity: "low", labelEn: "Overripe", labelEs: "Sobremaduro", impactEs: "tendría un impacto leve en el precio final del lote", impactEn: "would have a slight impact on the final price of the lot" },
  { key: "redCells", severity: "medium", labelEn: "Red Cells", labelEs: "Células rojas (Red Cells)", impactEs: "puede tener un efecto menor en el precio final del lote", impactEn: "may have a minor effect on the final price of the lot" },
  { key: "freezingDamage", severity: "high", labelEn: "Freezing Damage", labelEs: "Daño por congelamiento", impactEs: "requiere atención especial, ya que puede afectar la aceptación del pallet y, por consecuencia, el precio final del lote", impactEn: "requires special attention, as it may affect pallet acceptance and, consequently, the final price of the lot" },
];

export const PROTECTION_DEFECT_SEVERITY = {
  high: { labelEs: "Severidad alta", labelEn: "High severity", color: "#B91C1C", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.35)" },
  mediumHigh: { labelEs: "Severidad media-alta", labelEn: "Medium-high severity", color: "#C2410C", bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.4)" },
  medium: { labelEs: "Severidad media", labelEn: "Medium severity", color: "#B45309", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)" },
  mediumLow: { labelEs: "Severidad media-baja", labelEn: "Medium-low severity", color: "#4D7C0F", bg: "rgba(132,204,22,0.12)", border: "rgba(132,204,22,0.4)" },
  low: { labelEs: "Severidad baja", labelEn: "Low severity", color: "#047857", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.35)" },
};

/* A pallet's defects are entries of { key, pct } — pct (0-100) is
   optional (2026-10-13, Arturo: "algunos defectos muestran
   porcentaje, debo tener la opcion de agregar el porcentaje de cada
   defecto en cada pallet o dejarlo en blanco si no lo informan" — the
   inspector's report sometimes states a defect rate, sometimes just
   names the defect with no number). Looks up the full defect
   definition for each entry and merges `pct` onto it; unknown keys
   are dropped (defensive — same pattern as the old defectsFromKeys). */
function defectsFromEntries(entries) {
  var list = Array.isArray(entries) ? entries : [];
  var out = [];
  list.forEach(function (e) {
    var key = e && typeof e === "object" ? e.key : e; /* tolerate a bare string too */
    var def = PROTECTION_DEFECTS.find(function (d) { return d.key === key; });
    if (def) {
      out.push(Object.assign({}, def, { pct: e && typeof e === "object" ? e.pct : undefined }));
      return;
    }
    /* 2026-10-27, Arturo: "necesito agregar... la opcion de agregar
       defectos que no esten listados ahi, y su severidad" — a defect
       outside the fixed catalog above carries its own typed name
       (`custom`) and chosen severity directly on the entry, since
       there's no PROTECTION_DEFECTS row to look either up from. Same
       generic impact-on-price wording already used by entries whose
       effect genuinely depends on reported severity (regression,
       chillingDamage). */
    if (e && typeof e === "object" && e.custom) {
      var sev = PROTECTION_DEFECT_SEVERITY[e.severity] ? e.severity : "medium";
      out.push({
        key: key, severity: sev, labelEn: e.custom, labelEs: e.custom,
        impactEs: "según la severidad, puede tener un efecto en el precio final del lote",
        impactEn: "depending on severity, it may have an effect on the final price of the lot",
        pct: e.pct,
      });
    }
  });
  return out;
}

export function selectedPalletDefects(pallet) {
  return defectsFromEntries(pallet && pallet.defects);
}

/* Union of every protected pallet's own defects PLUS the invoice's own
   whole-load defects, deduplicated by key (first pct seen wins — this
   list is only used for the invoice-wide outlook/summary paragraph,
   which reasons about severity, never percentage) — used for the
   invoice-wide outlook/summary paragraph (still one overall read on
   price risk), never for the per-pallet breakdown itself
   (selectedPalletDefects / buildProtectionReportRows carry that).

   2026-10-23, Arturo: Four Seasons never reports defects per pallet,
   only one aggregate list for the whole load (see QC Results PO
   2236319.pdf) — those live on invoice.loadDefects (same {key,pct}
   shape as a pallet's own `defects`), not on any single pallet
   allocation. Pallet-level `defects` stays in the union too, both for
   any older invoice that still has it and for Twin River's own
   per-pallet mirror. */
export function selectedProtectionDefects(invoice) {
  var rows = Array.isArray(invoice && invoice.palletAllocations) ? invoice.palletAllocations : [];
  var seen = {};
  var entries = [];
  rows.forEach(function (a) {
    (Array.isArray(a && a.defects) ? a.defects : []).forEach(function (e) {
      var key = e && typeof e === "object" ? e.key : e;
      if (!seen[key]) { seen[key] = true; entries.push(e); }
    });
  });
  (Array.isArray(invoice && invoice.loadDefects) ? invoice.loadDefects : []).forEach(function (e) {
    var key = e && typeof e === "object" ? e.key : e;
    if (!seen[key]) { seen[key] = true; entries.push(e); }
  });
  return defectsFromEntries(entries);
}

export function protectionDefectsOutlook(invoice) {
  var list = selectedProtectionDefects(invoice);
  if (list.length === 0) return "";
  var sevs = list.map(function (d) { return d.severity; });
  var hasHigh = sevs.indexOf("high") >= 0;
  var hasMedium = sevs.indexOf("mediumHigh") >= 0 || sevs.indexOf("medium") >= 0;
  var hasMold = list.some(function (d) { return d.key === "mold"; });
  var text = "Resumen: ";
  if (hasHigh) {
    text += "con base en los defectos registrados, el precio final de este lote podría verse ajustado.";
  } else if (hasMedium) {
    text += "con base en los defectos registrados, podría existir un ajuste moderado en el precio final de este lote.";
  } else {
    text += "con base en los defectos registrados, no se esperan cambios importantes en el precio final de este lote.";
  }
  if (hasMold) {
    text += " Los lotes con presencia de moho suelen someterse a un proceso de limpieza; sin embargo, este proceso no garantiza la aceptación del lote.";
  }
  text += " La presente información es orientativa. La decisión final sobre la aceptación del lote corresponde al cliente final (retailer).";
  return text;
}

/* 2026-10-13, Arturo: "los defectos de calidad deben ser a nivel
   pallet, uno podria tener cierto tipo de defectos y otro pallet
   distintos" — grouped by pallet now instead of one flat list for the
   whole invoice, so the shipper can see exactly which pallet had
   which defect, not just an undifferentiated combined list. */
export function protectionDefectsEmailText(invoice) {
  var rows = Array.isArray(invoice && invoice.palletAllocations)
    ? invoice.palletAllocations.filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas") && Array.isArray(a.defects) && a.defects.length > 0; })
    : [];
  /* 2026-10-23, Arturo: Four Seasons reports one aggregate defect list
     for the whole load, never per pallet (see QC Results PO
     2236319.pdf) — printed as its own flat section, not grouped under
     a "Pallet X" header like Twin River's real per-pallet data. */
  var loadDefects = defectsFromEntries(invoice && invoice.loadDefects);
  if (rows.length === 0 && loadDefects.length === 0) return "";
  var out = "Defectos encontrados:\n";
  rows.forEach(function (a) {
    out += "Pallet " + (a.palletNum || "—") + ":\n";
    defectsFromEntries(a.defects).forEach(function (d) {
      var pctTxt = (d.pct === 0 || d.pct) ? " — " + d.pct + "%" : "";
      out += "- " + d.labelEn + pctTxt + " (" + PROTECTION_DEFECT_SEVERITY[d.severity].labelEs.toLowerCase() + "): " + d.impactEs + ".\n";
    });
  });
  loadDefects.forEach(function (d) {
    var pctTxt = (d.pct === 0 || d.pct) ? " — " + d.pct + "%" : "";
    out += "- " + d.labelEn + pctTxt + " (" + PROTECTION_DEFECT_SEVERITY[d.severity].labelEs.toLowerCase() + "): " + d.impactEs + ".\n";
  });
  var outlook = protectionDefectsOutlook(invoice);
  return out + "\n" + outlook + "\n\n";
}

export function buildProtectionReportHTML(invoice, shipment, supplier, company, notifiedAt, clientArrivalDate, clientArrivalTime) {
  var rows = buildProtectionReportRows(invoice, shipment);
  var shipperName = (supplier && (supplier.fullName || supplier.shortName)) || (invoice && invoice.shipperName) || "—";
  var shipperAddrParts = supplier ? [supplier.address, [supplier.city, supplier.state].filter(Boolean).join(", "), supplier.country].filter(Boolean) : [];
  var protectedCases = rows.reduce(function (s, r) { return s + (parseInt(r.casesPallet, 10) || 0); }, 0);
  var protectionText = (company && company.protectionNoticeEs) || "";
  var arrivalText = clientArrivalDate
    ? (new Date(clientArrivalDate + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }) + (clientArrivalTime ? " " + clientArrivalTime : ""))
    : "—";

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Protection Notice — ' + escHtml(invoice.puNumber || "") + '</title><style>';
  h += '@page{size:letter;margin:0.55in 0.6in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4;padding-top:14px}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #1B3A4B}';
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:24px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:10px;color:#666;text-align:right;margin-top:4px}';
  h += '.parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:14px 0}';
  h += '.party{padding:10px 14px;border:1px solid #e0e0e0;border-radius:6px;background:#fafbfc}';
  h += '.party .p-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.party .p-name{font-size:13px;font-weight:700;color:#1B3A4B;margin-bottom:2px}';
  h += '.party .p-addr{font-size:9px;color:#666;line-height:1.6}';
  h += '.meta-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin:0 0 12px;padding:12px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B;border-right:4px solid #1B3A4B}';
  h += '.meta-grid .label{font-size:7.5px;text-transform:uppercase;letter-spacing:0.5px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.notice-box{margin:0 0 14px;padding:12px 14px;background:#FFF8E1;border:1px solid #F5C244;border-radius:6px;font-size:10px;color:#6b4e00;white-space:pre-line}';
  h += '.notice-box b{display:block;font-size:10px;margin-bottom:4px;letter-spacing:0.5px}';
  h += '.defect-box{margin:0 0 14px;padding:10px 14px;background:#fff;border:1px solid #d0d5dd;border-left:4px solid #B91C1C;border-radius:6px;font-size:10px;color:#1a1a1a}';
  h += '.defect-title{font-size:9px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700;color:#B91C1C;margin-bottom:6px}';
  h += '.defect-row{margin:4px 0;line-height:1.5}';
  h += '.defect-pill{display:inline-block;padding:1px 7px;margin-right:6px;border-radius:8px;border:1px solid;font-size:8px;font-weight:700}';
  h += '.outlook-box{margin:12px 0 0;border-left-color:#1B3A4B;font-size:9.5px;line-height:1.5}';
  h += '.remarks-box{border-left-color:#1B3A4B}.remarks-box .defect-title{color:#1B3A4B}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:5px 6px;font-size:7px;text-transform:uppercase;letter-spacing:0.5px;color:#fff;background:#1B3A4B;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:4px 6px;border-bottom:1px solid #eee;font-size:9px;vertical-align:middle}';
  h += '.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  h += '.totals{margin-top:10px;padding:8px 14px;background:#1B3A4B;color:#fff;border-radius:6px;display:flex;justify-content:space-between;font-size:10px;font-weight:600}';
  h += '.footer{margin-top:22px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';
  h += '<script>try{window.scrollTo(0,0);document.documentElement.scrollTop=0;document.body.scrollTop=0;}catch(e){}</script>';

  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">AVISO DE PROTECCIÓN</div>';
  h += '<div class="doc-sub">Notificado: ' + escHtml(fmtDateTimeLong(notifiedAt)) + '</div></div></div>';

  h += '<div class="parties">';
  h += '<div class="party"><div class="p-label">De</div><div class="p-name">' + escHtml(GFI.name) + '</div>';
  h += '<div class="p-addr">' + GFI.lines.map(escHtml).join("<br/>") + '</div></div>';
  h += '<div class="party"><div class="p-label">Para (Shipper)</div><div class="p-name">' + escHtml(shipperName) + '</div>';
  if (shipperAddrParts.length) h += '<div class="p-addr">' + shipperAddrParts.map(escHtml).join("<br/>") + '</div>';
  h += '</div></div>';

  /* Meta grid — PU#/PO# (2026-09-14 follow-up: "agregar el PO mio y
     PU tanto en el cuerpo del mensaje... como en el reporte pdf...
     antes de el detalle de los pallets") plus Shipper Invoice #,
     Proforma #, and Lot, all already in the email body — repeated
     here so the PDF reads standalone once forwarded/printed. */
  h += '<div class="meta-grid">';
  h += '<div><div class="label">PU#</div><div class="value">' + escHtml(invoice.puNumber || "—") + '</div></div>';
  h += '<div><div class="label">PO#</div><div class="value">' + escHtml(invoice.clientRef || "—") + '</div></div>';
  h += '<div><div class="label">Factura Shipper #</div><div class="value">' + escHtml((shipment && shipment.shipperInvoice) || "—") + '</div></div>';
  h += '<div><div class="label">Proforma #</div><div class="value">' + escHtml((shipment && shipment.proforma) || "—") + '</div></div>';
  h += '<div><div class="label">Lote</div><div class="value">' + escHtml(invoice.lot || (shipment && shipment.lot) || "—") + '</div></div>';
  h += '<div><div class="label">Llegada con Cliente</div><div class="value">' + escHtml(arrivalText) + '</div></div>';
  h += '</div>';

  if (protectionText) {
    /* 2026-09-15 (Arturo): protectionText already starts with "AVISO DE
       PROTECCIÓN:" on its own — a hardcoded <b> heading here duplicated
       the phrase (same fix as emailHelpers.js's wrapProtectionNotice). */
    h += '<div class="notice-box">' + escHtml(protectionText) + '</div>';
  }

  /* 2026-10-13, Arturo: "los defectos de calidad deben ser a nivel
     pallet, uno podria tener cierto tipo de defectos y otro pallet
     distintos" — grouped by pallet (reusing `rows`, already carrying
     each pallet's own defects) instead of one combined list for the
     whole invoice. */
  var rowsWithDefects = rows.filter(function (r) { return Array.isArray(r.defects) && r.defects.length > 0; });
  /* 2026-10-23, Arturo: Four Seasons' own QC report (QC Results PO
     2236319.pdf) is one aggregate test for the whole load, never
     broken down per pallet — printed as its own flat section below any
     real per-pallet data (Twin River), not grouped under a pallet
     number it was never actually tied to. */
  var loadDefectsForPdf = defectsFromEntries(invoice.loadDefects);
  if (rowsWithDefects.length > 0 || loadDefectsForPdf.length > 0) {
    h += '<div class="defect-box"><div class="defect-title">Defectos encontrados</div>';
    rowsWithDefects.forEach(function (r) {
      h += '<div class="defect-row" style="margin-bottom:6px"><b style="display:block;margin-bottom:2px">Pallet ' + escHtml(r.palletNum) + '</b>';
      defectsFromEntries(r.defects).forEach(function (d) {
        var s = PROTECTION_DEFECT_SEVERITY[d.severity];
        var pctTxt = (d.pct === 0 || d.pct) ? ' <span style="font-weight:700">(' + escHtml(d.pct) + '%)</span>' : "";
        h += '<div><span class="defect-pill" style="color:' + s.color + ';background:' + s.bg + ';border-color:' + s.border + '">' + escHtml(s.labelEs) + '</span>';
        h += '<b>' + escHtml(d.labelEn) + '</b>' + pctTxt + ' — ' + escHtml(d.impactEs) + '</div>';
      });
      h += '</div>';
    });
    if (loadDefectsForPdf.length > 0) {
      h += '<div class="defect-row" style="margin-bottom:6px">';
      loadDefectsForPdf.forEach(function (d) {
        var s = PROTECTION_DEFECT_SEVERITY[d.severity];
        var pctTxt = (d.pct === 0 || d.pct) ? ' <span style="font-weight:700">(' + escHtml(d.pct) + '%)</span>' : "";
        h += '<div><span class="defect-pill" style="color:' + s.color + ';background:' + s.bg + ';border-color:' + s.border + '">' + escHtml(s.labelEs) + '</span>';
        h += '<b>' + escHtml(d.labelEn) + '</b>' + pctTxt + ' — ' + escHtml(d.impactEs) + '</div>';
      });
      h += '</div>';
    }
    h += '</div>';
  }

  if (invoice.clientRemarksSummary) {
    h += '<div class="defect-box remarks-box"><div class="defect-title">Client Quality Inspector Remarks</div><div style="white-space:pre-line">' + escHtml(invoice.clientRemarksSummary) + '</div></div>';
  }

  h += '<table class="items"><thead><tr>';
  h += '<th class="c">Pallet #</th>';
  h += '<th class="c">Warehouse Tag ID</th>';
  h += '<th>Product</th>';
  h += '<th class="c">Cat</th>';
  h += '<th class="c">Condition</th>';
  h += '<th class="c">Pack</th>';
  h += '<th>Product Description</th>';
  h += '<th class="c">Units/Case</th>';
  h += '<th class="c">Cases/Pallet</th>';
  h += '<th class="c">Label</th>';
  h += '<th class="c">Variety</th>';
  h += '<th class="c">Status</th>';
  h += '</tr></thead><tbody>';
  rows.forEach(function (r, i) {
    var bg = i % 2 === 1 ? ' style="background:#fafbfc"' : '';
    h += '<tr' + bg + '>';
    h += '<td class="c mono">' + escHtml(r.palletNum) + '</td>';
    h += '<td class="c mono">' + escHtml(r.tagId) + '</td>';
    h += '<td>' + escHtml(r.productBase) + '</td>';
    h += '<td class="c">' + escHtml(r.category) + '</td>';
    h += '<td class="c">' + escHtml(r.condition) + '</td>';
    h += '<td class="c">' + escHtml(r.packType) + '</td>';
    h += '<td>' + escHtml(r.productDescription) + '</td>';
    h += '<td class="c">' + escHtml(r.unitsCase) + '</td>';
    h += '<td class="c">' + escHtml(r.casesPallet) + '</td>';
    h += '<td class="c">' + escHtml(r.labelInitials) + '</td>';
    h += '<td class="c">' + escHtml(r.variety) + '</td>';
    h += '<td class="c">' + statusCellHtml(r.saleStatus) + '</td>';
    h += '</tr>';
    if (shipment && shipment.texasQcMode === "clarifresh") h += qcDefectsRowHtml(r.qcDefects, 12);
  });
  h += '</tbody></table>';

  h += '<div class="totals"><span>Pallets protegidos: ' + rows.length + '</span><span>Cajas protegidas: ' + protectedCases + '</span></div>';
  var outlook = protectionDefectsOutlook(invoice);
  if (outlook) h += '<div class="defect-box outlook-box">' + escHtml(outlook) + '</div>';

  h += '<div class="footer">Ground Fresh International, Inc. · 152 W Hoffman Ave Ste 5, Lindenhurst, NY 11757 · ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

/* Promise<Blob> builder — reused by the preview modal's download
   button AND by InvoiceRegistry.js's buildAttachments so the emailed
   copy and the manually-downloaded copy are byte-identical. */
export function buildProtectionReportBlob(invoice, shipment, supplier, company, notifiedAt, clientArrivalDate, clientArrivalTime) {
  var html = buildProtectionReportHTML(invoice, shipment, supplier, company, notifiedAt, clientArrivalDate, clientArrivalTime);
  return htmlToPdfBlob(html);
}

/* "Protection Notice {PU#} {Shipper Invoice #} {Shipper Name}" per
   spec — safeFilename sanitises spaces/punctuation for the
   filesystem, same helper paymentConfirmationFilename uses. */
export function protectionReportFilename(invoice, shipment, supplier) {
  var shipperName = (supplier && (supplier.fullName || supplier.shortName)) || (invoice && invoice.shipperName) || "";
  var shipperInvoice = (shipment && shipment.shipperInvoice) || "";
  return safeFilename("Protection Notice " + (invoice.puNumber || "") + " " + shipperInvoice + " " + shipperName) + ".pdf";
}

/* React preview modal — same iframe + Print/Download/Close shape as
   paymentConfirmation.js's PaymentConfirmationPreview. */
export function ProtectionReportPreview({ invoice, shipment, supplier, company, notifiedAt, clientArrivalDate, clientArrivalTime, onClose }) {
  var html = buildProtectionReportHTML(invoice, shipment, supplier, company, notifiedAt, clientArrivalDate, clientArrivalTime);
  var printDoc = function () {
    var w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(function () { w.print(); }, 400);
  };
  var [pdfBusy, setPdfBusy] = useState(false);
  var handleDownloadPdf = async function () {
    setPdfBusy(true);
    try {
      var blob = await buildProtectionReportBlob(invoice, shipment, supplier, company, notifiedAt, clientArrivalDate, clientArrivalTime);
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = protectionReportFilename(invoice, shipment, supplier);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF error: " + (e.message || String(e)));
    } finally {
      setPdfBusy(false);
    }
  };
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 20002, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 60, overflowY: "auto" }}>
      <div style={{ width: 800, maxWidth: "97vw", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden", marginBottom: 40 }}>
        <div style={{ padding: "10px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Protection Notice Report — PU# {invoice.puNumber}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={printDoc} style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Print / Save PDF</button>
            <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #1B3A4B", background: "transparent", color: "#1B3A4B", cursor: pdfBusy ? "wait" : "pointer", fontSize: 12, fontWeight: 600 }}>{pdfBusy ? "…" : "💾 Download PDF"}</button>
            <button onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>Close</button>
          </div>
        </div>
        <div style={{ padding: 0, background: "#fff" }}>
          <iframe
            title="protection-report"
            srcDoc={html}
            onLoad={function (e) { try { var w = e.target.contentWindow; if (w) { w.scrollTo(0, 0); } } catch (err) {} }}
            style={{ width: "100%", height: "calc(100vh - 110px)", minHeight: 500, border: "none", display: "block" }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

/* Read-only "Client Responses — Protection" summary for Bill and
   Shipment detail views (2026-10-07, Arturo: "al igual que rejections,
   mandalo visible a bills y embarques debajo de rejections en cada
   uno"). Same collapsible shape as RejectionDetailsReadOnly in
   rejectionReport.js — collapsed by default, one row per response
   across every invoice tied to this lot. Four Seasons invoices only
   ever have the single mirrored field (never the array); every other
   client may have several — both read the same way the invoice's own
   card does (array first, legacy single field as a one-item fallback). */
var RESPONSE_LABELS = {
  es: { willPayFull: "Pago Completo", settled: "Precio Ajustado", noResultYet: "Aún sin Resultado" },
  en: { willPayFull: "Full Pay", settled: "Settled", noResultYet: "No Result Yet" },
};

/* Cheap "does this lot have any recorded response" check, same
   array/legacy-field fallback as the component below — used by
   BillRegistry.js's Lot Activity title-bar button (2026-10-16) to
   decide whether to show its small activity marker, without having
   to render (or duplicate) the full read-only list just to find out. */
export function hasProtectionResponses(lot, invoices) {
  return (invoices || []).some(function (inv) {
    if (String(inv.lot || "") !== String(lot || "")) return false;
    if (inv.protectionResponses && inv.protectionResponses.some(function (r) { return r && r.status; })) return true;
    return !!inv.protectionResponseStatus;
  });
}

export function ProtectionResponsesReadOnly({ invoices, lot, lang }) {
  var [open, setOpen] = useState(false);
  var labels = RESPONSE_LABELS[lang] || RESPONSE_LABELS.en;
  var rows = [];
  (invoices || []).filter(function (inv) { return String(inv.lot || "") === String(lot || ""); }).forEach(function (inv) {
    var list = (inv.protectionResponses && inv.protectionResponses.length > 0)
      ? inv.protectionResponses
      : (inv.protectionResponseStatus ? [{ status: inv.protectionResponseStatus, note: inv.protectionResponseNote || "", at: inv.protectionResponseAt || "" }] : []);
    list.forEach(function (r, idx) {
      if (!r || !r.status) return;
      rows.push({ pu: inv.puNumber || "—", n: idx + 1, status: r.status, note: r.note || "", at: r.at });
    });
  });
  if (rows.length === 0) return null;
  var fmt = function (v) { return v ? new Date(v).toLocaleString() : "—"; };
  return (
    <div style={{ marginBottom: 14, borderRadius: 8, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.04)" }}>
      <button type="button" onClick={function () { setOpen(!open); }} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        <span>{lang === "es" ? "Respuestas del Cliente — Protección" : "Client Responses — Protection"}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "0 12px 10px" }}>
      {rows.map(function (r, i) {
        return (
          <div key={i} style={{ fontSize: 12, padding: "6px 0", borderTop: i > 0 ? "1px solid rgba(220,38,38,0.15)" : "none" }}>
            <div><strong>{"PU# " + r.pu + " · Response " + r.n}</strong>{" — " + (labels[r.status] || r.status)}</div>
            {r.note && <div style={{ marginTop: 3, whiteSpace: "pre-line" }}>{r.note}</div>}
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>{fmt(r.at)}</div>
          </div>
        );
      })}
      </div>}
    </div>
  );
}
